import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../api';
import { BRAND_NAME } from '../brand';
import Modal from './Modal';
import AdminAddressAutocomplete from './AdminAddressAutocomplete';
import ProductSearchDropdown from './ProductSearchDropdown';
import BouquetSearchDropdown from './BouquetSearchDropdown';
import AssemblyGroupBuilder from './AssemblyGroupBuilder';
import DatePickerCalendar from './DatePickerCalendar';
import Toggle from './Toggle';
import Tooltip from './Tooltip';
import ConfirmDialog from './ConfirmDialog';
import { TrashIcon } from './ActionIcons';
import { todayMSK, todayMSKString, currentHourMSK, currentMinutesMSK } from '../utils/timezone';
import { useDeliverySlots, slotGridCols } from '../deliverySlots';
import { deliveryOutOfRangeInfo, deliveryOutOfRangeText } from '../utils';


const PAYMENT_METHODS = [
  { value: 'QR', label: 'QR' },
  { value: 'Наличные', label: 'Наличные' },
  { value: 'Карта', label: 'Карта' },
  { value: 'Р/с', label: 'Р/с' },
];

// Телефон: state хранит только цифры абонента (до 10), отображение через displayPhone
function parsePhoneInput(raw, prevDigits) {
  let d = raw.replace(/\D/g, '');
  // Убираем код страны (7 или 8)
  if (d.length >= 1 && (d[0] === '7' || d[0] === '8')) d = d.slice(1);
  d = d.slice(0, 10);
  // Если кол-во цифр не уменьшилось при удалении (застряли на скобке) — убираем последнюю цифру
  if (prevDigits && d === prevDigits && raw.length < displayPhone(prevDigits).length) {
    d = d.slice(0, -1);
  }
  return d;
}

function displayPhone(digits) {
  if (!digits || digits.length === 0) return '';
  let r = '+7 (' + digits.slice(0, 3);
  if (digits.length >= 3) r += ') ';
  if (digits.length > 3) r += digits.slice(3, 6);
  if (digits.length > 6) r += '-' + digits.slice(6, 8);
  if (digits.length > 8) r += '-' + digits.slice(8, 10);
  return r;
}

// formatPhone — для инициализации из сохранённых данных ("+7 (xxx)..." → цифры)
function formatPhone(value) {
  return parsePhoneInput(value);
}

function getTodayStr() {
  return todayMSKString();
}

// ---- styles ----
const ACCENT = 'var(--primary-color)';  // #1C3D2E (deep forest green)
const sectionStyle = { marginBottom: 12 };
// Лейблы — мягкий брендовый стиль (без CAPS, читается как ярлык, не как заголовок)
const labelStyle = { display: 'block', fontSize: 12, color: 'var(--primary-color)', marginBottom: 4, fontWeight: 600 };
const inputStyle = {
  width: '100%', padding: '8px 12px', fontSize: 14,
  border: '1px solid #D8DDDA', '--ch': '5px', '--stroke': '#D8DDDA', outline: 'none', boxSizing: 'border-box',
  color: '#1C3D2E', background: '#fff',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s, --stroke 0.15s',
};
// Переключатели «одно из» — общий .seg/.seg-btn (UX-аудит 25.10), а не свой стиль на странице
const segBtn = (active) => `seg-btn${active ? ' on' : ''}`;
const hintStyle = { fontSize: 11, color: '#5E6561', marginTop: 2 };
const errorTextStyle = { fontSize: 13, color: '#B0603C', marginTop: 2 };
const itemRowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '8px 0', borderBottom: '1px solid #EBEDEC', fontSize: 13,
};
// «−/+» у количества — общий .btn-icon; здесь только размер 30×30 и кегль знака
const qtyBtnStyle = { width: 30, height: 30, fontSize: 16, flexShrink: 0 };
const totalRowStyle = { display: 'flex', justifyContent: 'space-between', fontSize: 15, padding: '4px 0' };

// Section — обёртка для группировки полей с тонким разделителем сверху и заголовком.
// Использовать как: <Section title="Доставка">…</Section>
function Section({ title, children, style }) {
  return (
    <section style={{
      borderTop: '1px solid #EAEAE5', paddingTop: 14, marginTop: 14,
      ...style,
    }}>
      {title && (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary-color)',
                      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
          {title}
        </div>
      )}
      {children}
    </section>
  );
}

function AdminOrderCreateModal({ onClose, onCreated, showToast }) {
  const { timeSlots: TIME_SLOTS } = useDeliverySlots();
  // Tab
  const [tab, setTab] = useState('walkin'); // walkin | client | internal
  const scrollContainerRef = useRef(null);

  // Settings
  const [settings, setSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  // Окно «Загрузка...» показываем, только если настройки грузятся дольше 400 мс:
  // при обычной скорости оно мелькало на ~50 мс перед формой (моргание при открытии).
  const [showSettingsLoader, setShowSettingsLoader] = useState(false);
  useEffect(() => {
    if (!settingsLoading) return undefined;
    const t = setTimeout(() => setShowSettingsLoader(true), 400);
    return () => clearTimeout(t);
  }, [settingsLoading]);

  // Client tab: user search
  const [platform, setPlatform] = useState('telegram');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Phone
  const [phone, setPhone] = useState('');

  // Email (client)
  const [email, setEmail] = useState('');

  // Delivery
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [address, setAddress] = useState('');
  const [addressDadata, setAddressDadata] = useState('');
  const [cityDistrict, setCityDistrict] = useState('');
  const [deliveryLat, setDeliveryLat] = useState(null);
  const [deliveryLon, setDeliveryLon] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [deliveryCostManual, setDeliveryCostManual] = useState(false);
  const [deliveryCalcLoading, setDeliveryCalcLoading] = useState(false);
  const [deliveryOutOfRange, setDeliveryOutOfRange] = useState(null); // { distance, maxKm }

  // Address details
  const [apartment, setApartment] = useState('');
  const [entrance, setEntrance] = useState('');
  const [floor, setFloor] = useState('');
  const [intercom, setIntercom] = useState('');
  const [noApartment, setNoApartment] = useState(false);

  // Recipient (internal / client+walkin with delivery)
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [isRecipientOther, setIsRecipientOther] = useState(false);

  // Customer info (walkin with delivery)
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Items
  const [items, setItems] = useState([]);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showBouquetSearch, setShowBouquetSearch] = useState(false);

  // Bonuses (client only)
  const [useBonuses, setUseBonuses] = useState(false);

  // Postcard (client only)
  const [hasPostcard, setHasPostcard] = useState(false);
  const [greeting, setGreeting] = useState('');

  // Packaging
  const [packagingType, setPackagingType] = useState('none');
  // Полный список из /packaging (без скрытых), отсортирован по sort_order.
  // Элемент: { type, name, quantity, is_hidden, sort_order, ... }
  const [packagingList, setPackagingList] = useState([]);

  // Odd quantity
  const [oddQuantity, setOddQuantity] = useState(false);

  // Marketing (internal only)
  const [isMarketing, setIsMarketing] = useState(false);

  // Internal payment
  const [internalPaymentRequired, setInternalPaymentRequired] = useState(false);
  const [internalPaymentAmount, setInternalPaymentAmount] = useState('');

  // Walkin (legacy state — больше не используется в UI, оставлено для совместимости payload)
  const [walkinType, setWalkinType] = useState('bunch');
  const [paymentMethod, setPaymentMethod] = useState('');

  // Сборные букеты: [{sequence_no, florist_notes, items: [{product_id, product_name, price, quantity}]}]
  const [assemblies, setAssemblies] = useState([]);

  // Deficit confirmation
  const [deficitConfirm, setDeficitConfirm] = useState(null);

  // Calendar colored dates (available-dates based on cart)
  const [calendarDates, setCalendarDates] = useState(null); // { green: [], yellow: [] }

  // Comments
  const [adminComment, setAdminComment] = useState('');
  const [courierComment, setCourierComment] = useState('');

  // Доступное количество товаров на выбранную дату доставки (для бейджа в карточке товара)
  const [productsAvailability, setProductsAvailability] = useState({});

  // Inline-алерт у кнопки "+ Добавить" если дата не выбрана
  const [showDateAlert, setShowDateAlert] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const todayStr = getTodayStr();

  // Переключение вкладки не стирает уже введённое: доставка, дата, адрес, получатель,
  // состав, открытка, комментарии — общие блоки, они есть на всех трёх вкладках и
  // видны оператору. Payload собирается строго по активной вкладке, так что перенос
  // ничего лишнего в заказ не тащит. Сбрасываем только служебное состояние UI.
  const handleTabSwitch = (newTab) => {
    if (newTab === tab) return;
    const prevTab = tab;
    setTab(newTab);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    setWalkinType(newTab === 'client' ? 'bunch' : 'bouquet');
    setErrors({});
    setShowProductSearch(false);
    setShowBouquetSearch(false);
    // Упаковка: у витрины дефолт «без коробки», у остальных — транспортировочная.
    // Явный выбор оператора сохраняем, нетронутый дефолт меняем на дефолт новой вкладки.
    const defaultPackaging = (t) => (t === 'walkin' ? 'none' : 'transport');
    setPackagingType(prev => (prev === defaultPackaging(prevTab) ? defaultPackaging(newTab) : prev));
    // На внутренней вкладке получатель обязателен и живёт без тумблера. Уходя с неё,
    // включаем тумблер «Получатель — другой человек», иначе заполненное имя/телефон
    // остались бы в стейте, но пропали с экрана.
    if (prevTab === 'internal' && (recipientName.trim() || recipientPhone)) setIsRecipientOther(true);

    // Телефон и имя у вкладок лежат в разных полях: витрина — покупатель, за клиента —
    // телефон клиента, внутренний — получатель. Оператор вводит их один раз, поэтому
    // переносим введённое в поле новой вкладки, если оно пустое (заполненное не трогаем).
    const phoneOf = { walkin: customerPhone, client: phone, internal: recipientPhone };
    const nameOf = { walkin: customerName, client: '', internal: recipientName };
    const carriedPhone = phoneOf[prevTab] || '';
    const carriedName = (nameOf[prevTab] || '').trim();
    if (carriedPhone) {
      if (newTab === 'walkin' && !customerPhone) setCustomerPhone(carriedPhone);
      if (newTab === 'client' && !phone) setPhone(carriedPhone);
      if (newTab === 'internal' && !recipientPhone) setRecipientPhone(carriedPhone);
    }
    if (carriedName) {
      if (newTab === 'walkin' && !customerName.trim()) setCustomerName(carriedName);
      if (newTab === 'internal' && !recipientName.trim()) setRecipientName(carriedName);
    }
  };

  // Load settings on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await api.getSettings();
        // data — объект AppSettings { postcard_price, service_fee_percent, ... }
        const map = {};
        if (Array.isArray(data)) {
          data.forEach(s => { map[s.key] = s.value; });
        } else if (data && typeof data === 'object') {
          Object.entries(data).forEach(([k, v]) => { if (v != null) map[k] = String(v); });
        }
        setSettings(map);
        // Load packaging inventory (полный список, без скрытых) и сортируем по sort_order.
        try {
          const pkgData = await api.getPackagingInventory();
          const list = (Array.isArray(pkgData) ? pkgData : [])
            .filter(p => !p.is_hidden)
            .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
          setPackagingList(list);
        } catch { /* ignore */ }
      } catch { /* ignore */ }
      finally { setSettingsLoading(false); }
    })();
  }, []);

  // Auto-calculate delivery cost when address changes
  useEffect(() => {
    if (deliveryCostManual || deliveryType !== 'delivery') return;
    if (!deliveryLat || !deliveryLon) {
      // Нет координат — не можем рассчитать, предлагаем ввести вручную
      if (address && address.length > 10) {
        setDeliveryOutOfRange({ distance: '?', maxKm: '?' });
        setDeliveryCostManual(true);
      }
      return;
    }
    let cancelled = false;
    setDeliveryCalcLoading(true);
    api.calculateDelivery(addressDadata || address, deliveryLat, deliveryLon)
      .then(result => {
        if (cancelled) return;
        if (result.requires_negotiation) {
          setDeliveryOutOfRange(deliveryOutOfRangeInfo(result));
          setDeliveryCost(0);
          setDeliveryCostManual(true);
        } else {
          setDeliveryOutOfRange(null);
          setDeliveryCost(result.price || 0);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDeliveryCalcLoading(false); });
    return () => { cancelled = true; };
  }, [address, addressDadata, deliveryLat, deliveryLon, deliveryType, deliveryCostManual]);

  // Recalculate calendar dates based on cart items
  useEffect(() => {
    if (items.length === 0) { setCalendarDates(null); return; }
    const timer = setTimeout(() => {
      // Для календаря — только продуктовые позиции; букеты пропускаем (бэк handles bouquet availability отдельно)
      const cartItems = items.filter(i => !i.bouquet_id).map(i => ({ product_id: i.product_id, quantity: i.quantity }));
      api.getAvailableDates(cartItems, 30)
        .then(result => {
          const availDates = result.dates || [];
          // Generate all dates for next 30 days — по МСК
          const allDates = [];
          const today = todayMSK();
          for (let i = 0; i < 30; i++) {
            const d = new Date(today);
            d.setDate(d.getDate() + i);
            allDates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
          }
          const green = availDates;
          const yellow = allDates.filter(d => !availDates.includes(d));
          setCalendarDates({ green, yellow });
        })
        .catch(() => setCalendarDates(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [items]);

  // Clear apartment when noApartment is checked
  useEffect(() => {
    if (noApartment) setApartment('');
  }, [noApartment]);

  // Clear past time slot when selecting today (МСК — магазин в Москве)
  useEffect(() => {
    if (deliveryDate === todayStr && deliveryTime) {
      const [, endPart] = deliveryTime.split('-');
      const [endH, endM] = endPart.split(':').map(Number);
      const nowMinutes = currentHourMSK() * 60 + currentMinutesMSK();
      const endMinutes = endH * 60 + endM;
      if (nowMinutes >= endMinutes) {
        setDeliveryTime('');
      }
    }
  }, [deliveryDate, todayStr, deliveryTime]);

  // Load per-product date_available map when deliveryDate changes
  // Используется для отображения "доступно N шт" рядом с названием товара
  useEffect(() => {
    if (!deliveryDate) {
      setProductsAvailability({});
      return;
    }
    // Дата выбрана — скрываем алерт у кнопки "+ Добавить"
    setShowDateAlert(false);
    let cancelled = false;
    api.getProducts(1, 500, false, deliveryDate).then(data => {
      if (cancelled) return;
      const list = data.items || data || [];
      const map = {};
      list.forEach(p => {
        if (p.date_available != null) map[p.id] = p.date_available;
      });
      setProductsAvailability(map);
    }).catch(() => {
      if (!cancelled) setProductsAvailability({});
    });
    return () => { cancelled = true; };
  }, [deliveryDate]);

  // User search (fix #7: useCallback)
  const handleUserSearch = useCallback(async () => {
    if (!userSearchQuery.trim()) return;
    setUserSearching(true);
    setUserSearchResults([]);
    try {
      const q = userSearchQuery.trim();
      const params = { limit: 10 };
      if (platform === 'telegram') {
        if (/^\d+$/.test(q)) {
          params.searchTelegramId = q;
        } else {
          params.searchUsername = q;
        }
      } else {
        params.searchMaxId = q;
      }
      const result = await api.getUsers(params);
      const users = result.users || [];
      setUserSearchResults(users);
      if (users.length === 0) {
        setErrors(prev => ({ ...prev, user: 'Пользователь не найден' }));
      }
    } catch { setUserSearchResults([]); }
    finally { setUserSearching(false); }
  }, [userSearchQuery, platform]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserSearchResults([]);
    setUserSearchQuery('');
  };

  // Items management (fix #7: useCallback)
  const handleAddProduct = useCallback((product) => {
    setItems(prev => [...prev, {
      product_id: product.id,
      name: product.name,
      price: product.price,
      quantity: product.step || 1,
      step: product.step || 1,
      max_qty: null, // Админ может выбрать любое количество
    }]);
    setShowProductSearch(false);
  }, []);

  const handleAddBouquet = useCallback((bouquet) => {
    setItems(prev => [...prev, {
      product_id: null,
      bouquet_id: bouquet.bouquet_id,
      name: bouquet.name,
      price: bouquet.price,
      quantity: 1,
      step: 1,
      max_qty: null,
      // bouquet-специфичные поля для UI:
      kind: bouquet.kind || 'recipe',
      bouquet_components: bouquet.components || [],
      available_for_date: bouquet.available_for_date ?? 0,
      available_physical: bouquet.available_physical ?? 0,
      assembled_only: !!bouquet.assembled_only,
      components_status: bouquet.components_status || [],
    }]);
    setShowBouquetSearch(false);
  }, []);

  // Админ меняет по одной штуке (шаг — правило витрины для клиента; первая строка
  // всё же ставит «шаг» штук, как в корзине, дальше — по 1).
  const handleChangeQty = useCallback((idx, delta) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const newQty = item.quantity + delta;
      if (newQty < 1) return item;
      if (item.max_qty != null && newQty > item.max_qty) return item;
      return { ...item, quantity: newQty };
    }));
  }, []);

  const handleRemoveItem = useCallback((idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // === Assemblies (сборные букеты) handlers ===
  const handleAddAssembly = useCallback(() => {
    setAssemblies(prev => [
      ...prev,
      { sequence_no: prev.length + 1, florist_notes: '', items: [] },
    ]);
  }, []);

  const handleRemoveAssembly = useCallback((groupIdx) => {
    setAssemblies(prev => prev
      .filter((_, i) => i !== groupIdx)
      .map((g, i) => ({ ...g, sequence_no: i + 1 }))
    );
  }, []);

  const handleAssemblyAddStem = useCallback((groupIdx, stem) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, items: [...g.items, stem] } : g
    ));
  }, []);

  const handleAssemblyRemoveStem = useCallback((groupIdx, stemIdx) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, items: g.items.filter((_, k) => k !== stemIdx) } : g
    ));
  }, []);

  const handleAssemblyChangeQty = useCallback((groupIdx, stemIdx, qty) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx
        ? { ...g, items: g.items.map((s, k) => k === stemIdx ? { ...s, quantity: qty } : s) }
        : g
    ));
  }, []);

  const handleAssemblyChangeNotes = useCallback((groupIdx, notes) => {
    setAssemblies(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, florist_notes: notes } : g
    ));
  }, []);

  // fix #6: useMemo for calculations.
  // Стебли (loose, без assembly) считаются по service_fee_percent.
  // Стебли в сборном букете И каталожные букеты — по bouquet_service_fee_percent.
  const { postcardPrice, effectiveDeliveryCost, bonusDiscount, total } = useMemo(() => {
    const stemsLooseSubtotal = items.filter(i => !i.bouquet_id).reduce((sum, item) => sum + item.price * item.quantity, 0);
    const catalogBouquetsSubtotal = items.filter(i => i.bouquet_id).reduce((sum, item) => sum + item.price * item.quantity, 0);
    const assemblyStemsSubtotal = assemblies.reduce(
      (sum, g) => sum + g.items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0),
      0
    );
    const bouquetsSubtotal = catalogBouquetsSubtotal + assemblyStemsSubtotal;
    const subtotal = stemsLooseSubtotal + bouquetsSubtotal;
    const postcardPrice = hasPostcard ? parseFloat(settings['postcard_price'] || '0') : 0;
    const stemsFeePct = parseFloat(settings['service_fee_percent'] || '11');
    const bouquetsFeePct = parseFloat(settings['bouquet_service_fee_percent'] || '20');
    const stemsFee = Math.round(stemsLooseSubtotal * stemsFeePct / 100);
    const bouquetsFee = Math.round(bouquetsSubtotal * bouquetsFeePct / 100);
    const serviceFee = stemsFee + bouquetsFee;
    const effectiveDeliveryCost = deliveryType === 'delivery' ? deliveryCost : 0;
    const bonusDiscount = (tab === 'client' && useBonuses && selectedUser)
      ? Math.min(selectedUser.bonus_balance || 0, subtotal + postcardPrice + serviceFee) : 0;
    const total = subtotal + serviceFee + effectiveDeliveryCost + postcardPrice - bonusDiscount;
    return {
      subtotal,
      stemsSubtotal: stemsLooseSubtotal,
      bouquetsSubtotal,
      postcardPrice,
      serviceFeePercent: stemsFeePct,
      stemsFee, bouquetsFee, serviceFee,
      effectiveDeliveryCost, bonusDiscount, total,
    };
  }, [items, assemblies, tab, hasPostcard, settings, deliveryType, deliveryCost, useBonuses, selectedUser]);

  // Детализация заказа по позициям: каждая позиция = товар + сервисный сбор на неё.
  // Сбор распределяется по позициям так, чтобы сумма всех allIn равнялась
  // subtotal + serviceFee (агрегатное округление как на бэке) — без расхождения с «Итого».
  const detailLines = useMemo(() => {
    const stemsFeePct = parseFloat(settings['service_fee_percent'] || '11');
    const bouquetsFeePct = parseFloat(settings['bouquet_service_fee_percent'] || '20');
    const lines = [];
    items.filter(i => !i.bouquet_id).forEach(it => {
      lines.push({ key: `s-${it.product_id}`, name: it.name, qty: it.quantity, cost: it.price * it.quantity, cat: 'stems', pct: stemsFeePct });
    });
    items.filter(i => i.bouquet_id).forEach(it => {
      lines.push({ key: `b-${it.bouquet_id}`, name: it.name, qty: it.quantity, cost: it.price * it.quantity, cat: 'bouquet', pct: bouquetsFeePct });
    });
    assemblies.forEach((g, idx) => {
      const cost = g.items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);
      if (cost > 0) lines.push({ key: `a-${idx}`, name: `Сборный букет ${idx + 1}`, qty: null, cost, cat: 'bouquet', pct: bouquetsFeePct });
    });
    // Распределение агрегатного сбора по категории (largest remainder).
    const distribute = (cat, pct) => {
      const catLines = lines.filter(l => l.cat === cat);
      const catSubtotal = catLines.reduce((s, l) => s + l.cost, 0);
      if (catSubtotal === 0) { catLines.forEach(l => { l.fee = 0; }); return; }
      const aggFee = Math.round(catSubtotal * pct / 100);
      const raw = catLines.map(l => ({ l, exact: l.cost * pct / 100 }));
      let assigned = 0;
      raw.forEach(r => { r.fee = Math.floor(r.exact); assigned += r.fee; });
      let remainder = aggFee - assigned;
      raw.sort((a, b) => (b.exact - Math.floor(b.exact)) - (a.exact - Math.floor(a.exact)));
      for (let i = 0; i < remainder && raw.length > 0; i++) raw[i % raw.length].fee += 1;
      raw.forEach(r => { r.l.fee = r.fee; });
    };
    distribute('stems', stemsFeePct);
    distribute('bouquet', bouquetsFeePct);
    return lines.map(l => ({ ...l, allIn: l.cost + (l.fee || 0) }));
  }, [items, assemblies, settings]);

  // Validation
  const validate = () => {
    const errs = {};
    // Заказ непустой если есть обычные позиции ИЛИ хотя бы один сборный букет со стеблями
    const hasAnyItems = items.length > 0
      || assemblies.some(g => g.items && g.items.length > 0);
    // Адрес вне тарифной сетки: расчёт ставит 0 и просит цену вручную. Пустое поле
    // выглядит как «0» и молча сохранялось (заказ № 6046, Куюки, 23.8 км при сетке
    // до 21) — у клиента и walkin доставку платят, без цены не пускаем.
    // Внутренний заказ — цена всегда вручную, 0 там допустим.
    if ((tab === 'client' || tab === 'walkin') && deliveryType === 'delivery'
        && deliveryOutOfRange && !(Number(deliveryCost) > 0)) {
      errs.deliveryCost = 'Адрес вне зоны доставки — укажите стоимость доставки';
    }
    if (tab === 'client') {
      if (!selectedUser) errs.user = 'Выберите клиента';
      if (!email.trim()) errs.email = 'Укажите email для оплаты';
      if (!phone || phone.length < 10) errs.phone = 'Укажите телефон клиента';
      if (!deliveryDate) errs.date = 'Выберите дату';
      if (!deliveryTime) errs.time = 'Выберите время';
      if (deliveryType === 'delivery') {
        if (!address.trim()) errs.address = 'Укажите адрес';
        if (!noApartment && !apartment.trim()) errs.apartment = 'Укажите квартиру или отметьте \'Нет квартиры\'';
      }
      if (isRecipientOther) {
        if (!recipientName.trim()) errs.recipientName = 'Укажите имя получателя';
        if (!recipientPhone || recipientPhone.length < 10) errs.recipientPhone = 'Укажите телефон получателя';
      }
      if (!hasAnyItems) errs.items = 'Добавьте товары';
    }
    if (tab === 'internal') {
      if (!recipientName.trim()) errs.recipientName = 'Укажите имя получателя';
      if (!recipientPhone || recipientPhone.length < 10) errs.recipientPhone = 'Укажите телефон получателя';
      if (!deliveryDate) errs.date = 'Выберите дату доставки';
      if (!deliveryTime) errs.time = 'Выберите время доставки';
      if (deliveryType === 'delivery') {
        if (!address.trim()) errs.address = 'Укажите адрес доставки';
        if (!noApartment && !apartment.trim()) errs.apartment = 'Укажите квартиру или отметьте \'Нет квартиры\'';
      }
      // Если включён "Требуется оплата" — сумма обязательна и > 0
      if (internalPaymentRequired) {
        const amt = parseInt(internalPaymentAmount);
        if (!amt || amt <= 0) errs.internalPaymentAmount = 'Укажите сумму к оплате (больше 0)';
      }
    }
    if (tab === 'walkin') {
      if (!hasAnyItems) errs.items = 'Добавьте товары';
      if (!paymentMethod) errs.payment = 'Выберите способ оплаты';
      if (deliveryType === 'delivery') {
        if (!address.trim()) errs.address = 'Укажите адрес';
        if (!noApartment && !apartment.trim()) errs.apartment = 'Укажите квартиру или отметьте \'Нет квартиры\'';
        // Поля получателя обязательны только если включён toggle "Получатель — другой человек"
        if (isRecipientOther) {
          if (!recipientName.trim()) errs.recipientName = 'Укажите имя получателя';
          if (!recipientPhone || recipientPhone.length < 10) errs.recipientPhone = 'Укажите телефон получателя';
        }
      }
      if (!deliveryDate) errs.date = 'Выберите дату';
      if (!deliveryTime) errs.time = 'Выберите время';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit (fix #1: double-submit guard)
  const handleSubmit = async () => {
    if (submitting) return;
    if (!validate()) return;

    // Проверка дефицита — запрашиваем доступность только по товарам из заказа
    if (deliveryDate && items.length > 0) {
      try {
        const stemItems = items.filter(i => !i.bouquet_id && i.product_id);
        const stockMap = stemItems.length > 0
          ? (await api.getAvailableMaxQtyBatch(stemItems.map(i => i.product_id), deliveryDate)).results || {}
          : {};
        const stemDeficits = stemItems
          .filter(i => (i.quantity || 0) > (stockMap[String(i.product_id)] || 0))
          .map(i => {
            const avail = stockMap[String(i.product_id)] || 0;
            return `${i.name} — дефицит ${i.quantity - avail} шт`;
          });
        // Букеты: дефицит recipe-части per-component. В create-модалке available_on_date
        // НЕ включает собственный спрос (cartItemsPending = только другие позиции), поэтому
        // self_demand НЕ прибавляем (в отличие от edit). needRecipe = qty − собранные экземпляры.
        const bouquetDeficits = [];
        items.forEach(it => {
          if (!it.bouquet_id) return;
          const cs = it.components_status || [];
          if (!cs.length) return;
          const qty = parseInt(it.quantity) || 1;
          const physAvail = it.available_physical != null
            ? it.available_physical
            : (it.kind === 'assembled' ? qty : 0);
          const needRecipe = Math.max(0, qty - Math.min(qty, physAvail));
          if (needRecipe <= 0) return;
          cs.forEach(c => {
            const recipeNeeded = (c.needed_per_bouquet || 0) * needRecipe;
            const avail = c.available_on_date || 0;
            const def = Math.max(0, recipeNeeded - Math.max(0, avail));
            if (def > 0) bouquetDeficits.push(`${it.name} → ${c.name}: дефицит ${def} шт`);
          });
        });
        // assembled_only: букет «только готовые» — админу добор по рецепту разрешён, но предупреждаем.
        const assembledOnlyWarnings = [];
        items.forEach(it => {
          if (!it.bouquet_id || !it.assembled_only) return;
          const qty = parseInt(it.quantity) || 1;
          const phys = it.available_physical != null ? it.available_physical : 0;
          const over = qty - phys;
          if (over > 0) assembledOnlyWarnings.push(`${it.name}: «только готовые» — готовых ${Math.max(0, phys)} шт, ещё ${over} соберётся по рецепту`);
        });
        const names = [...stemDeficits, ...bouquetDeficits, ...assembledOnlyWarnings];
        if (names.length > 0) {
          setDeficitConfirm({
            names,
            date: deliveryDate,
            onConfirm: () => { setDeficitConfirm(null); doSubmit(); }
          });
          return;
        }
      } catch { /* ignore, proceed */ }
    }
    doSubmit();
  };

  const doSubmit = async () => {
    setSubmitting(true);

    const data = {
      admin_order_type: tab,
      delivery_type: deliveryType,
      delivery_date: deliveryDate || null,
      delivery_time: deliveryTime || null,
      delivery_cost: effectiveDeliveryCost,
      packaging_type: packagingType,
      admin_comment: adminComment || null,
      courier_comment: courierComment.trim() || null,
      items: items.map(i => i.bouquet_id
        ? { bouquet_id: i.bouquet_id, quantity: i.quantity }
        : { product_id: i.product_id, quantity: i.quantity }),
      assemblies: assemblies
        .filter(g => g.items && g.items.length > 0)
        .map((g, idx) => ({
          sequence_no: idx + 1,
          florist_notes: g.florist_notes && g.florist_notes.trim() ? g.florist_notes.trim() : null,
          items: g.items.map(it => ({
            product_id: it.product_id,
            quantity: it.quantity,
            price: it.price,
          })),
        })),
    };

    if (deliveryType === 'delivery') {
      data.delivery_address = address;
      data.delivery_address_dadata = addressDadata || address;
      data.city_district = cityDistrict || null;
      data.delivery_lat = deliveryLat;
      data.delivery_lon = deliveryLon;
      data.apartment = noApartment ? null : (apartment || null);
      data.no_apartment = noApartment;
      data.entrance = entrance || null;
      data.floor = floor || null;
      data.intercom = intercom || null;
    }

    if (tab === 'client') {
      data.source_platform = platform;
      data.platform_user_id = platform === 'telegram'
        ? String(selectedUser.telegram_id || selectedUser.id)
        : String(selectedUser.max_id || selectedUser.id);
      data.phone = phone ? displayPhone(phone) : null;
      data.email = email;
      data.use_bonuses = useBonuses;
      data.odd_quantity = oddQuantity;
      if (hasPostcard) {
        data.greeting = greeting || null;
        data.postcard_price = postcardPrice;
      }
      if (isRecipientOther) {
        data.is_recipient_other = true;
        data.recipient_name = recipientName || null;
        data.recipient_phone = recipientPhone ? displayPhone(recipientPhone) : null;
      }
    }

    if (tab === 'internal') {
      data.phone = null;
      data.recipient_name = recipientName;
      data.recipient_phone = recipientPhone;
      data.is_marketing = isMarketing;
      data.is_recipient_other = true;
      data.delivery_cost = parseFloat(deliveryCost) || 0;
      data.odd_quantity = oddQuantity;
      if (hasPostcard) {
        data.greeting = greeting || null;
        data.postcard_price = postcardPrice;
      }
      if (internalPaymentRequired && internalPaymentAmount > 0) {
        data.client_payment_amount = parseInt(internalPaymentAmount);
      }
    }

    if (tab === 'walkin') {
      data.payment_method = paymentMethod;
      data.paid_at = new Date().toISOString();
      data.odd_quantity = oddQuantity;
      if (hasPostcard) {
        data.greeting = greeting || null;
        data.postcard_price = postcardPrice;
      }
      data.phone = customerPhone ? displayPhone(customerPhone) : null;
      if (customerName.trim()) {
        data.customer_name = customerName.trim();
      }
      // Получатель — другой человек: только если toggle включён.
      // Если выключен — recipient = customer (бэк сам подставит из customer_name/phone).
      if (deliveryType === 'delivery' && isRecipientOther) {
        data.is_recipient_other = true;
        data.recipient_name = recipientName || null;
        data.recipient_phone = recipientPhone ? displayPhone(recipientPhone) : null;
      } else {
        data.is_recipient_other = false;
      }
    }

    // Источники резервирования резолвятся бэком автоматически: latest-eligible
    // плановая → старее → текущий склад (см. resolve_stock_sources).

    try {
      const result = await api.createAdminOrder(data);
      if (result.warnings && result.warnings.length > 0) {
        result.warnings.forEach(w => showToast(w, 'warning'));
      }
      onCreated(result.order || result);
      onClose();
    } catch (err) {
      showToast(err.message || 'Ошибка создания заказа', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset delivery fields when switching to pickup
  useEffect(() => {
    if (deliveryType === 'pickup') {
      setAddress('');
      setAddressDadata('');
      setDeliveryLat(null);
      setDeliveryLon(null);
      setDeliveryCost(0);
    }
  }, [deliveryType]);

  // ---- Render sections ----

  const renderRecipientOther = () => (
    <>
      <div style={sectionStyle}>
        <Toggle checked={isRecipientOther} onChange={setIsRecipientOther} label="Получатель — другой человек" />
      </div>
      {isRecipientOther && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelStyle}>Имя получателя *</label>
            <input type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)}
              placeholder="ФИО получателя" style={inputStyle} />
            {errors.recipientName && <div style={errorTextStyle}>{errors.recipientName}</div>}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelStyle}>Телефон получателя *</label>
            <input type="text" value={displayPhone(recipientPhone)} onChange={e => { setRecipientPhone(parsePhoneInput(e.target.value, recipientPhone)); }}
              placeholder="+7 (___) ___-__-__" style={inputStyle} />
            {errors.recipientPhone && <div style={errorTextStyle}>{errors.recipientPhone}</div>}
          </div>
        </div>
      )}
    </>
  );

  const renderPostcardAndOptions = () => (
    <div style={{ display: 'flex', gap: 24, marginBottom: 4, flexWrap: 'wrap' }}>
      <Toggle
        checked={hasPostcard}
        onChange={setHasPostcard}
        label={postcardPrice > 0 ? `Открытка (${postcardPrice} ₽)` : 'Открытка'}
      />
      <Toggle checked={oddQuantity} onChange={setOddQuantity} label="Нечётное количество" />
    </div>
  );

  const renderGreetingInput = () => (
    hasPostcard ? (
      <div style={{ marginBottom: 8 }}>
        <textarea value={greeting} onChange={e => setGreeting(e.target.value)}
          placeholder="Текст поздравления..." rows={2}
          style={{ ...inputStyle, resize: 'vertical' }} />
      </div>
    ) : null
  );

  const renderWalkinCustomerInfo = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <label style={labelStyle}>Имя заказчика</label>
        <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
          placeholder="ФИО" style={inputStyle} />
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <label style={labelStyle}>Телефон заказчика</label>
        <input type="text" value={displayPhone(customerPhone)} onChange={e => { setCustomerPhone(parsePhoneInput(e.target.value, customerPhone)); }}
          placeholder="+7 (___) ___-__-__" style={inputStyle} />
      </div>
    </div>
  );

  const renderWalkinRecipientInfo = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <label style={labelStyle}>Имя получателя *</label>
        <input type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)}
          placeholder="ФИО получателя" style={inputStyle} />
        {errors.recipientName && <div style={errorTextStyle}>{errors.recipientName}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <label style={labelStyle}>Телефон получателя *</label>
        <input type="text" value={displayPhone(recipientPhone)} onChange={e => { setRecipientPhone(parsePhoneInput(e.target.value, recipientPhone)); }}
          placeholder="+7 (___) ___-__-__" style={inputStyle} />
        {errors.recipientPhone && <div style={errorTextStyle}>{errors.recipientPhone}</div>}
      </div>
    </div>
  );

  const renderDeliveryForm = (showManualCost = false) => (
    <>
      {/* Адрес + Кв/Подъезд/Этаж/Домофон + "Нет кв." — всё компактно */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 3, minWidth: 240 }}>
          <label style={labelStyle}>Адрес доставки *</label>
          <AdminAddressAutocomplete
            value={address}
            onChange={(addr, data) => {
              setAddress(addr);
              setDeliveryCostManual(false);
              setDeliveryOutOfRange(null);
              if (data) {
                setAddressDadata(data.unrestricted_value || data.value || addr);
                setCityDistrict(data.city_district || '');
                setDeliveryLat(data.geo_lat ? parseFloat(data.geo_lat) : null);
                setDeliveryLon(data.geo_lon ? parseFloat(data.geo_lon) : null);
              } else {
                // Очистка координат при ручном вводе/удалении
                setAddressDadata('');
                setDeliveryLat(null);
                setDeliveryLon(null);
                setDeliveryCost(0);
              }
            }}
          />
        </div>
        <div style={{ width: 70 }}>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 4 }}>
            Кв.
            <label style={{ fontSize: 11, color: '#5E6561', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, fontWeight: 400 }}>
              <input type="checkbox" checked={noApartment} onChange={e => { setNoApartment(e.target.checked); if (e.target.checked) setApartment(''); }} style={{ accentColor: '#1C3D2E' }} />
              нет
            </label>
          </label>
          <input type="text" value={apartment} onChange={e => setApartment(e.target.value)}
            style={inputStyle} disabled={noApartment} />
        </div>
        <div style={{ width: 75 }}>
          <label style={labelStyle}>Подъезд</label>
          <input type="text" value={entrance} onChange={e => setEntrance(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ width: 55 }}>
          <label style={labelStyle}>Этаж</label>
          <input type="text" value={floor} onChange={e => setFloor(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ width: 90 }}>
          <label style={labelStyle}>Домофон</label>
          <input type="text" value={intercom} onChange={e => setIntercom(e.target.value)} style={inputStyle} />
        </div>
      </div>
      {errors.address && <div style={{ ...errorTextStyle, marginTop: -4, marginBottom: 4 }}>{errors.address}</div>}
      {errors.apartment && <div style={{ ...errorTextStyle, marginTop: -2, marginBottom: 4 }}>{errors.apartment}</div>}

      {/* Стоимость доставки */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ ...labelStyle, marginBottom: 0 }}>Доставка:</span>
          {deliveryCalcLoading ? (
            <span style={{ color: '#5E6561' }}>...</span>
          ) : (showManualCost || deliveryCostManual) ? (
            <>
              <input type="text" inputMode="numeric" pattern="[0-9]*"
                value={deliveryCost === 0 ? '' : String(deliveryCost)}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '');
                  setDeliveryCost(v === '' ? 0 : parseInt(v, 10));
                  if (errors.deliveryCost) setErrors(prev => ({ ...prev, deliveryCost: undefined }));
                }}
                onBlur={e => { if (!e.target.value) setDeliveryCost(0); }}
                placeholder="0"
                style={{ ...inputStyle, width: 100, fontWeight: 600 }} />
              <span style={{ color: '#5E6561' }}>₽</span>
            </>
          ) : (
            <span style={{ fontWeight: 600 }}>{deliveryCost} ₽</span>
          )}
          {!showManualCost && !deliveryOutOfRange && (
            <label style={{ fontSize: 12, color: '#5E6561', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={deliveryCostManual} onChange={e => { setDeliveryCostManual(e.target.checked); if (!e.target.checked) setDeliveryOutOfRange(null); }} style={{ accentColor: '#1C3D2E' }} />
              вручную
            </label>
          )}
        </div>
        {deliveryOutOfRange && (
          <div style={{ fontSize: 12, color: '#B0603C', marginTop: 4 }}>
            {deliveryOutOfRangeText(deliveryOutOfRange)}
          </div>
        )}
        {errors.deliveryCost && <div style={{ ...errorTextStyle, marginTop: 4 }}>{errors.deliveryCost}</div>}
      </div>

    </>
  );

  const renderDateTimePicker = () => (
    <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ width: 170, flexShrink: 0 }}>
        <label style={labelStyle}>Дата *</label>
        <DatePickerCalendar
          value={deliveryDate}
          onChange={(date) => setDeliveryDate(date)}
          minDate={todayStr}
          coloredDates={calendarDates}
          compact
        />
        {errors.date && <div style={errorTextStyle}>{errors.date}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 280 }}>
        <label style={labelStyle}>Слот *</label>
        {/* Слоты — один переключатель .seg: срез только у контейнера, между слотами
            разделители, без промежутков; не влезли — переносятся (владелец 25.10) */}
        {/* Слоты — сеткой, ряды ровные (6 → 3 + 3, 8 → 4 + 4): «повисшие» во второй строке смотрелись
            сломанными; линии и между строками, и между колонками (владелец 25–26.10) */}
        <div className={`seg seg-grid c${slotGridCols(TIME_SLOTS.length)}`} role="group" aria-label="Слот доставки">
          {TIME_SLOTS.map(slot => {
            const isToday = deliveryDate === todayStr;
            let isPast = false;
            if (isToday) {
              const [, endPart] = slot.split('-');
              const [endH, endM] = endPart.split(':').map(Number);
              const nowMinutes = currentHourMSK() * 60 + currentMinutesMSK();
              const endMinutes = endH * 60 + endM;
              isPast = nowMinutes >= endMinutes;
            }
            const isActive = deliveryTime === slot;
            return (
              <button key={slot} type="button" onClick={() => {
                  if (isPast) return;
                  setDeliveryTime(slot);
                  if (errors.time) setErrors(prev => ({ ...prev, time: undefined }));
                }}
                disabled={isPast}
                className={segBtn(isActive)}
                style={{
                  whiteSpace: 'nowrap', padding: '7px 6px',
                  ...(isPast ? { opacity: 0.4, textDecoration: 'line-through', cursor: 'not-allowed' } : null),
                }}>
                {slot}
              </button>
            );
          })}
        </div>
        {errors.time && <div style={errorTextStyle}>{errors.time}</div>}
      </div>
    </div>
  );

  const renderItems = (optional = false) => (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>
          Товары {optional && <span style={{ fontWeight: 400, color: '#5E6561' }}>(можно добавить позже)</span>}
        </label>
        <Tooltip text={!deliveryDate ? 'Сначала выберите дату — без неё нельзя проверить остатки' : ''}>
          {/* Ссылки-действия .btn-link — как в правке заказа (UX-аудит 25.10); без даты клик
              показывает подсказку ниже, а не красит кнопки терракотой */}
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <button type="button" className="btn-link" style={{ fontSize: 13 }} onClick={() => {
                if (!deliveryDate) { setShowDateAlert(true); return; }
                setShowProductSearch(true);
              }}>
              + Добавить стебли
            </button>
            <button type="button" className="btn-link" style={{ fontSize: 13 }} onClick={() => {
                if (!deliveryDate) { setShowDateAlert(true); return; }
                setShowBouquetSearch(true);
              }}>
              + Добавить букет
            </button>
            <button type="button" className="btn-link" style={{ fontSize: 13 }} onClick={() => {
                if (!deliveryDate) { setShowDateAlert(true); return; }
                handleAddAssembly();
              }}>
              + Добавить сборный букет
            </button>
          </div>
        </Tooltip>
        {showDateAlert && !deliveryDate && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, color: '#B0603C',
            background: '#FFE7DD', padding: '5px 10px', '--ch': '5px', '--stroke': '#ED956F',
            border: '1px solid #ED956F',
          }}>
            <span style={{
              display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
              width: 18, height: 18, borderRadius: '50%',
              background: '#B0603C', color: '#fff', fontWeight: 700, fontSize: 12,
              flexShrink: 0,
            }}>!</span>
            <span>Сначала выберите дату выполнения заказа</span>
          </div>
        )}
      </div>

      {showProductSearch && (
        <div style={{ marginBottom: 8 }}>
          <ProductSearchDropdown
            onSelect={handleAddProduct}
            excludeIds={items.filter(i => !i.bouquet_id).map(i => i.product_id)}
            onClose={() => setShowProductSearch(false)}
            selectedDate={deliveryDate || null}
          />
        </div>
      )}
      {showBouquetSearch && (
        <div style={{ marginBottom: 8 }}>
          <BouquetSearchDropdown
            onSelect={handleAddBouquet}
            onClose={() => setShowBouquetSearch(false)}
            deliveryDate={deliveryDate || null}
            excludeBouquetIds={items.filter(i => i.bouquet_id).map(i => i.bouquet_id)}
            cartItemsPending={items.map(i => i.bouquet_id ? ({
              bouquet_id: i.bouquet_id,
              quantity: i.quantity,
              components: (i.bouquet_components || []).map(c => ({ product_id: c.product_id, quantity: c.quantity })),
            }) : ({
              product_id: i.product_id,
              quantity: i.quantity,
            }))}
          />
        </div>
      )}

      {items.length === 0 && assemblies.length === 0 && !optional && errors.items && (
        <div style={errorTextStyle}>{errors.items}</div>
      )}

      {assemblies.map((grp, gIdx) => (
        <AssemblyGroupBuilder
          key={`asm-${gIdx}`}
          sequenceNo={gIdx + 1}
          items={grp.items}
          floristNotes={grp.florist_notes}
          selectedDate={deliveryDate || null}
          onAddStem={(s) => handleAssemblyAddStem(gIdx, s)}
          onRemoveStem={(sIdx) => handleAssemblyRemoveStem(gIdx, sIdx)}
          onChangeQty={(sIdx, q) => handleAssemblyChangeQty(gIdx, sIdx, q)}
          onChangeNotes={(notes) => handleAssemblyChangeNotes(gIdx, notes)}
          onRemoveGroup={() => handleRemoveAssembly(gIdx)}
        />
      ))}

      {items.map((item, idx) => {
        const isBouquet = !!item.bouquet_id;
        const avail = isBouquet ? item.available_for_date : productsAvailability[item.product_id];
        const showAvail = deliveryDate && avail != null;
        // Inline deficit для букета: дефицит recipe-части. available_on_date в create уже
        // «исключая себя» (cartItemsPending = только др. позиции) → self_demand НЕ прибавляем.
        const bqQty = parseInt(item.quantity) || 1;
        const bqPhysAvail = item.available_physical != null
          ? item.available_physical
          : (item.kind === 'assembled' ? bqQty : 0);
        const bqNeedRecipe = Math.max(0, bqQty - Math.min(bqQty, bqPhysAvail));
        const bouquetDeficits = (isBouquet && bqNeedRecipe > 0)
          ? (item.components_status || []).map(c => {
              const recipeNeeded = (c.needed_per_bouquet || 0) * bqNeedRecipe;
              const avail = c.available_on_date || 0;
              const def = Math.max(0, recipeNeeded - Math.max(0, avail));
              return def > 0 ? { name: c.name, deficit: def } : null;
            }).filter(Boolean)
          : [];
        // «только готовые»: сколько штук уйдёт в recipe-добор сверх готовых экземпляров.
        const assembledOnlyOver = (isBouquet && item.assembled_only)
          ? Math.max(0, bqQty - (item.available_physical != null ? item.available_physical : 0))
          : 0;
        return (
        <div key={isBouquet ? `b-${item.bouquet_id}` : `p-${item.product_id}`} style={itemRowStyle}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{item.name}</span>
              {isBouquet && (
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', padding: '1px 5px', borderRadius: 3, background: '#FFE7DD', color: '#7E5D00' }}>Букет</span>
              )}
              {showAvail && (
                <span style={{
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '2px 8px',
                  '--ch': '8px',
                  background: avail >= item.quantity ? '#DFF2E8' : '#FFE7DD',
                  color: avail >= item.quantity ? '#1C3D2E' : '#B0603C',
                }}>
                  доступно {avail} шт
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#5E6561' }}>{item.price} ₽ / шт</div>
            {bouquetDeficits.length > 0 && (
              <div style={{ marginTop: 3, fontSize: 11, color: '#B0603C', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Дефицит:</span>
                {bouquetDeficits.map((d, i) => (
                  <span key={i}>{d.name} <strong>{d.deficit}</strong>{i < bouquetDeficits.length - 1 ? ',' : ''}</span>
                ))}
              </div>
            )}
            {assembledOnlyOver > 0 && (
              <div style={{ marginTop: 3, fontSize: 11, color: '#7E5D00' }}>
                <span style={{ fontWeight: 600 }}>Только готовые:</span> ещё {assembledOnlyOver} шт соберётся по рецепту
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* «−/+» и «Убрать» — общие .btn-icon; удаление — корзина, как в правке заказа (UX-аудит 25.10) */}
            <button type="button" className="btn-icon" style={qtyBtnStyle} onClick={() => handleChangeQty(idx, -1)}>−</button>
            <input type="number" min="1" value={item.quantity}
              onChange={e => {
                const raw = e.target.value;
                const val = raw === '' ? '' : parseInt(raw);
                setItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: val === '' ? '' : (val > 0 ? val : '') } : it));
              }}
              onBlur={() => {
                setItems(prev => prev.map((it, i) => i === idx && (!it.quantity || it.quantity < 1) ? { ...it, quantity: 1 } : it));
              }}
              style={{ width: 48, textAlign: 'center', fontWeight: 600, fontSize: 14, border: '1px solid #D8DDDA', '--ch': '5px', '--stroke': '#D8DDDA', padding: '2px 4px' }} />
            <button type="button" className="btn-icon" style={qtyBtnStyle} onClick={() => handleChangeQty(idx, 1)}>+</button>
            <span style={{ fontSize: 13, fontWeight: 500, minWidth: 60, textAlign: 'right' }}>
              {(item.price * item.quantity).toLocaleString('ru-RU')} ₽
            </span>
            <button type="button" className="btn-icon is-danger" onClick={() => handleRemoveItem(idx)}
              title="Убрать" aria-label="Убрать" style={{ flexShrink: 0 }}>
              <TrashIcon />
            </button>
          </div>
        </div>
        );
      })}
    </div>
  );

  const renderBonuses = () => {
    if (!selectedUser) return null;
    const balance = selectedUser.bonus_balance || 0;
    if (balance <= 0) return null;
    return (
      <div style={sectionStyle}>
        <Toggle
          checked={useBonuses}
          onChange={setUseBonuses}
          label={`Списать со счёта ${BRAND_NAME}`}
          hint={useBonuses ? `Будет списано ${bonusDiscount} ₽` : `${balance} ₽ доступно`}
        />
      </div>
    );
  };

  const renderPackaging = () => {
    return (
      <div style={{ ...sectionStyle, marginTop: 24 }}>
        <label style={labelStyle}>Упаковка</label>
        <div className="seg">
          {/* «Без коробки» — всегда первая */}
          <button type="button"
            className={segBtn(packagingType === 'none')}
            onClick={() => setPackagingType('none')}>
            Без коробки
          </button>
          {/* Все активные упаковки из packaging_inventory (по sort_order) */}
          {packagingList.map(pkg => {
            const qty = pkg.quantity;
            const outOfStock = qty != null && qty <= 0;
            const label = pkg.name || pkg.type;
            return (
              <button
                key={pkg.type}
                type="button" className={segBtn(packagingType === pkg.type)}
                style={outOfStock ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                onClick={() => !outOfStock && setPackagingType(pkg.type)}
                disabled={outOfStock}>
                {label}{qty != null ? ` (${qty})` : ''}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDeliveryTypeToggle = () => (
    <div style={sectionStyle}>
      <label style={labelStyle}>Тип получения</label>
      <div className="seg">
        <button type="button" className={segBtn(deliveryType === 'pickup')} onClick={() => setDeliveryType('pickup')}>
          Самовывоз
        </button>
        <button type="button" className={segBtn(deliveryType === 'delivery')} onClick={() => setDeliveryType('delivery')}>
          Доставка
        </button>
      </div>
    </div>
  );

  const renderTotals = () => (
    <div style={{ background: '#F4F4EF', '--ch': '8px', '--stroke': '#EBEDEC', padding: '14px 16px', marginBottom: 16, border: '1px solid #EBEDEC' }}>
      {/* Детализация по позициям: цена позиции = товары + сервисный сбор на неё.
          Чтобы можно было сразу назвать клиенту полную стоимость каждой позиции. */}
      {detailLines.map(line => (
        <div key={line.key} style={{ ...totalRowStyle, alignItems: 'flex-start' }}>
          <span style={{ flex: 1, paddingRight: 8 }}>
            {line.name}{line.qty != null ? ` (${line.qty} шт)` : ''}
            <span style={{ display: 'block', fontSize: 11, color: '#909894', fontWeight: 400 }}>
              {line.cost.toLocaleString('ru-RU')} ₽ + сбор {line.pct}% ({line.fee.toLocaleString('ru-RU')} ₽)
            </span>
          </span>
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{line.allIn.toLocaleString('ru-RU')} ₽</span>
        </div>
      ))}
      {postcardPrice > 0 && (
        <div style={totalRowStyle}>
          <span>Открытка</span>
          <span>{postcardPrice.toLocaleString('ru-RU')} ₽</span>
        </div>
      )}
      {effectiveDeliveryCost > 0 && (
        <div style={totalRowStyle}>
          <span>Доставка</span>
          <span>{effectiveDeliveryCost.toLocaleString('ru-RU')} ₽</span>
        </div>
      )}
      {bonusDiscount > 0 && (
        <div style={{ ...totalRowStyle, color: '#1C3D2E' }}>
          <span>Со счёта {BRAND_NAME}</span>
          <span>-{bonusDiscount.toLocaleString('ru-RU')} ₽</span>
        </div>
      )}
      <div style={{ ...totalRowStyle, fontWeight: 700, fontSize: 16, borderTop: '1px solid #d0d5dd', paddingTop: 8, marginTop: 6 }}>
        <span>Итого</span>
        <span>{total.toLocaleString('ru-RU')} ₽</span>
      </div>
      {tab === 'internal' && internalPaymentRequired && internalPaymentAmount > 0 && (
        <div style={{ ...totalRowStyle, fontWeight: 700, fontSize: 15, color: '#B0603C', paddingTop: 4 }}>
          <span>К оплате</span>
          <span>{Number(internalPaymentAmount).toLocaleString('ru-RU')} ₽</span>
        </div>
      )}
    </div>
  );

  const renderComments = () => (
    <div style={sectionStyle}>
      <label style={labelStyle}>Комментарий администратора</label>
      <textarea value={adminComment} onChange={e => setAdminComment(e.target.value)}
        placeholder="Внутренний комментарий..." rows={2}
        style={{ ...inputStyle, resize: 'vertical' }} />
      <label style={{ ...labelStyle, marginTop: 10 }}>Комментарий для курьера</label>
      <textarea value={courierComment} onChange={e => setCourierComment(e.target.value.slice(0, 100))}
        placeholder="Видит курьер: домофон, «позвонить за 30 минут»..." rows={2} maxLength={100}
        style={{ ...inputStyle, resize: 'vertical' }} />
      <div style={{ textAlign: 'right', fontSize: 11, color: courierComment.length >= 85 ? '#CB4D45' : '#999' }}>
        {courierComment.length}/100
      </div>
    </div>
  );

  // ---- Tab content ----

  const renderClientTab = () => (
    <>
      {/* Платформа */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>Платформа</label>
        <div className="seg">
          <button type="button" className={segBtn(platform === 'telegram')} onClick={() => { setPlatform('telegram'); setSelectedUser(null); setUserSearchQuery(''); setUserSearchResults([]); setUseBonuses(false); }}>Telegram</button>
          <button type="button" className={segBtn(platform === 'max')} onClick={() => { setPlatform('max'); setSelectedUser(null); setUserSearchQuery(''); setUserSearchResults([]); setUseBonuses(false); }}>Max</button>
        </div>
      </div>

      {/* Username/ID + Телефон + Email + Найти — одна строка */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, position: 'relative', minWidth: 220 }}>
          <label style={labelStyle}>{platform === 'telegram' ? 'Username / TG ID' : 'Max ID'}</label>
          {selectedUser ? (
            <div style={{
              ...inputStyle, display: 'flex', alignItems: 'center', gap: 8,
              background: '#DFF2E8', borderColor: ACCENT, '--stroke': ACCENT, padding: '6px 10px',
            }}>
              <span style={{ fontWeight: 600, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedUser.first_name || ''} {selectedUser.last_name || ''}{selectedUser.username ? ` (@${selectedUser.username})` : ''}
              </span>
              <span style={{ color: ACCENT, fontSize: 11, whiteSpace: 'nowrap' }}>
                {(selectedUser.bonus_balance || 0)} ₽
              </span>
              <button onClick={() => { setSelectedUser(null); setUseBonuses(false); setUserSearchQuery(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E6561', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>&times;</button>
            </div>
          ) : (
            <>
              <input type="text" value={userSearchQuery}
                onChange={e => setUserSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleUserSearch()}
                placeholder={platform === 'telegram' ? 'Username или ID' : 'Max ID'}
                style={inputStyle} />
              {/* Выпадающий список результатов */}
              {userSearchResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA', maxHeight: 150, overflowY: 'auto',
                  background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {userSearchResults.map(user => (
                    <div key={user.id}
                      onClick={() => handleSelectUser(user)}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleSelectUser(user)}
                      role="option" tabIndex={0}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #EBEDEC', display: 'flex', justifyContent: 'space-between' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F4F4EF'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <span>{user.first_name || ''} {user.last_name || ''}{user.username ? ` (@${user.username})` : ''}</span>
                      <span style={{ color: '#5E6561', fontSize: 11 }}>
                        {user.telegram_id ? `TG: ${user.telegram_id}` : ''}{user.max_id ? ` Max: ${user.max_id}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {/* Одна главная на окно — «Создать заказ»; поиск в контенте — второстепенная 38 px (UX-аудит 25.10) */}
        {!selectedUser && (
          <button type="button" className="btn btn-secondary btn-md" onClick={handleUserSearch} disabled={userSearching}
            style={{ whiteSpace: 'nowrap' }}>
            {userSearching ? '...' : 'Найти клиента'}
          </button>
        )}
        <div style={{ flex: 2, minWidth: 180 }}>
          <label style={labelStyle}>Телефон *</label>
          <input type="text" value={displayPhone(phone)} onChange={e => { setPhone(parsePhoneInput(e.target.value, phone)); }}
            placeholder="+7 (___) ___-__-__" style={inputStyle} />
          {errors.phone && <div style={errorTextStyle}>{errors.phone}</div>}
        </div>
        <div style={{ flex: 2, minWidth: 180 }}>
          <label style={labelStyle}>Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="email@example.com" style={inputStyle} />
        </div>
      </div>
      {errors.user && <div style={errorTextStyle}>{errors.user}</div>}
      {errors.email && <div style={errorTextStyle}>{errors.email}</div>}

      {renderRecipientOther()}
      {renderDeliveryTypeToggle()}
      {renderDateTimePicker()}
      {deliveryType === 'delivery' && renderDeliveryForm()}
      {renderItems()}
      {renderPostcardAndOptions()}
      {renderGreetingInput()}
      {renderBonuses()}
      {renderPackaging()}
    </>
  );

  const renderInternalTab = () => (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={labelStyle}>Имя получателя *</label>
          <input type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)}
            placeholder="ФИО получателя" style={inputStyle} />
          {errors.recipientName && <div style={errorTextStyle}>{errors.recipientName}</div>}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={labelStyle}>Телефон получателя *</label>
          <input type="text" value={displayPhone(recipientPhone)} onChange={e => { setRecipientPhone(parsePhoneInput(e.target.value, recipientPhone)); }}
            placeholder="+7 (___) ___-__-__" style={inputStyle} />
          {errors.recipientPhone && <div style={errorTextStyle}>{errors.recipientPhone}</div>}
        </div>
      </div>
      <div style={sectionStyle}>
        <Toggle
          checked={isMarketing}
          onChange={(v) => {
            setIsMarketing(v);
            if (v) { setInternalPaymentRequired(false); setInternalPaymentAmount(''); }
          }}
          label="Маркетинг"
          hint="Учитывается в рекламном бюджете"
        />
      </div>
      <div style={sectionStyle}>
        <Toggle
          checked={internalPaymentRequired}
          onChange={(v) => {
            setInternalPaymentRequired(v);
            if (!v) setInternalPaymentAmount('');
            if (v) setIsMarketing(false);
          }}
          label="Требуется оплата"
        />
        {internalPaymentRequired && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Сумма к оплате:</label>
              <input type="number" min="0" value={internalPaymentAmount}
                onChange={e => setInternalPaymentAmount(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                onBlur={() => { if (internalPaymentAmount === '') setInternalPaymentAmount(0); }}
                style={{ ...inputStyle, width: 120 }} />
              <span style={{ color: '#5E6561' }}>₽</span>
            </div>
            {errors.internalPaymentAmount && <div style={errorTextStyle}>{errors.internalPaymentAmount}</div>}
          </div>
        )}
      </div>
      {renderDeliveryTypeToggle()}
      {renderDateTimePicker()}
      {deliveryType === 'delivery' && renderDeliveryForm(true)}
      {renderItems(true)}
      {renderPostcardAndOptions()}
      {renderGreetingInput()}
      {renderPackaging()}
    </>
  );

  const renderWalkinTab = () => (
    <>
      {/* Имя и телефон клиента (опционально) */}
      {renderWalkinCustomerInfo()}

      {renderDeliveryTypeToggle()}
      {renderDateTimePicker()}
      {deliveryType === 'delivery' && (
        <>
          {/* Получатель — другой человек (опциональный блок) */}
          {renderRecipientOther()}
          {renderDeliveryForm()}
        </>
      )}

      {renderItems()}
      {renderPostcardAndOptions()}
      {renderGreetingInput()}
      {renderPackaging()}

      <div style={sectionStyle}>
        <label style={labelStyle}>Способ оплаты *</label>
        <div className="seg">
          {PAYMENT_METHODS.map(pm => (
            <button key={pm.value} onClick={() => setPaymentMethod(pm.value)}
              type="button" className={segBtn(paymentMethod === pm.value)}>
              {pm.label}
            </button>
          ))}
        </div>
        {errors.payment && <div style={errorTextStyle}>{errors.payment}</div>}
      </div>
    </>
  );

  if (settingsLoading) {
    if (!showSettingsLoader) return null;
    return (
      <Modal title="Новый заказ" onClose={onClose} size="large">
        <div style={{ padding: 40, textAlign: 'center', color: '#5E6561' }}>Загрузка...</div>
      </Modal>
    );
  }

  return (
    <>
    <Modal title="Новый заказ" onClose={onClose} size="large" confirmOverlayClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Создание...' : 'Создать заказ'}
          </button>
        </div>
      }>
      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab ${tab === 'walkin' ? 'active' : ''}`} onClick={() => handleTabSwitch('walkin')}>
          Трафик
        </button>
        <button className={`tab ${tab === 'client' ? 'active' : ''}`} onClick={() => handleTabSwitch('client')}>
          За клиента
        </button>
        <button className={`tab ${tab === 'internal' ? 'active' : ''}`} onClick={() => handleTabSwitch('internal')}>
          Внутренний
        </button>
      </div>

      {/* Tab content */}
      <div ref={scrollContainerRef} style={{ maxHeight: 'calc(80vh - 200px)', overflowY: 'auto', padding: '0 2px' }}>
        {tab === 'client' && renderClientTab()}
        {tab === 'internal' && renderInternalTab()}
        {tab === 'walkin' && renderWalkinTab()}

        {/* Shared */}
        {renderTotals()}
        {renderComments()}

      </div>
    </Modal>

    {/* Диалог подтверждения дефицита */}
    {deficitConfirm && (
      <ConfirmDialog
        title={`Дефицит на ${new Date(deficitConfirm.date + 'T00:00:00').toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' })}`}
        message={
          <>
            <div style={{ marginBottom: 8 }}>Следующие товары недоступны в нужном количестве:</div>
            <div style={{ fontSize: 13, color: '#1C3D2E' }}>
              {deficitConfirm.names.map((n, i) => (
                <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid #EBEDEC' }}>• {n}</div>
              ))}
            </div>
          </>
        }
        confirmText="Создать с дефицитом"
        onConfirm={deficitConfirm.onConfirm}
        onCancel={() => setDeficitConfirm(null)}
      />
    )}
    </>
  );
}

export default AdminOrderCreateModal;
